/**
 * BIBINONO Modern Product Card & Favorites Interactive Controller
 * Handles:
 * 1. Client-side Favorites/Wishlist Persistence (localStorage, aria-pressed, event dispatch)
 * 2. Header & Mobile Drawer reactive count badges
 * 3. Cross-tab synchronization via storage event
 * 4. Elegant Toast Notifications for add/remove feedback
 * 5. Multi-image Carousel / Preview Switcher via Dots (hover, tap, reset)
 * 6. Dynamic Section Re-hydration (MutationObserver & custom events)
 */

(function () {
  'use strict';

  if (window.__BIBINONO_CARD_INITIALIZED__) {
    if (window.BibinonoWishlist && typeof window.BibinonoWishlist.syncWishlistButtons === 'function') {
      window.BibinonoWishlist.syncWishlistButtons();
    }
    return;
  }
  window.__BIBINONO_CARD_INITIALIZED__ = true;

  const WISHLIST_STORAGE_KEY = 'bibinono_wishlist';

  // ── Toast Notification System ──
  let activeToastTimeout = null;

  function getToastContainer() {
    let container = document.getElementById('bibinono-toast-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'bibinono-toast-container';
      container.className = 'bibinono-toast-container';
      container.setAttribute('role', 'status');
      container.setAttribute('aria-live', 'polite');
      document.body.appendChild(container);
    }
    return container;
  }

  function dismissToast(toast) {
    if (!toast || toast.classList.contains('is-leaving')) return;
    toast.classList.remove('is-visible');
    toast.classList.add('is-leaving');
    setTimeout(() => {
      if (toast.parentElement) toast.remove();
    }, 280);
  }

  function showToast({ title, message, linkText, linkUrl, isError = false, isRemoved = false }) {
    const container = getToastContainer();

    // Immediately clear any active timer and dismiss previous toasts
    if (activeToastTimeout) {
      clearTimeout(activeToastTimeout);
      activeToastTimeout = null;
    }
    const existingToasts = container.querySelectorAll('.bibinono-toast');
    existingToasts.forEach((t) => dismissToast(t));

    const toast = document.createElement('div');
    toast.className = `bibinono-toast ${isError ? 'bibinono-toast--error' : ''} ${isRemoved ? 'bibinono-toast--removed' : ''}`;

    const isArabic = document.documentElement.lang === 'ar' || document.documentElement.dir === 'rtl';
    const defaultLinkText = isArabic ? 'عرض المفضلة' : 'View Favorites';
    const defaultLinkUrl = '/pages/wishlist';

    // Contextual icon
    let iconSvg = '';
    if (isError) {
      iconSvg = `
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D32F2F" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      `;
    } else if (isRemoved) {
      iconSvg = `
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8A8297" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
        </svg>
      `;
    } else {
      iconSvg = `
        <svg width="18" height="18" viewBox="0 0 24 24" fill="#E54848" stroke="#E54848" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
        </svg>
      `;
    }

    const hasLink = linkUrl !== false;
    const finalLinkUrl = typeof linkUrl === 'string' ? linkUrl : defaultLinkUrl;
    const finalLinkText = linkText || defaultLinkText;

    toast.innerHTML = `
      <div class="bibinono-toast__icon" aria-hidden="true">
        ${iconSvg}
      </div>
      <div class="bibinono-toast__content">
        <strong class="bibinono-toast__title">${title}</strong>
        ${message ? `<p class="bibinono-toast__msg">${message}</p>` : ''}
      </div>
      ${hasLink ? `<a href="${finalLinkUrl}" class="bibinono-toast__link" data-bibinono-wishlist-drawer-open>${finalLinkText}</a>` : ''}
      <button type="button" class="bibinono-toast__close" aria-label="Close notification">&times;</button>
    `;

    const closeBtn = toast.querySelector('.bibinono-toast__close');
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      dismissToast(toast);
    });

    container.appendChild(toast);

    // Trigger entrance animation
    requestAnimationFrame(() => {
      toast.classList.add('is-visible');
    });

    // Auto dismiss after 3200ms
    activeToastTimeout = setTimeout(() => {
      dismissToast(toast);
      activeToastTimeout = null;
    }, 3200);
  }

  // ── Wishlist / Favorites Manager ──
  function getWishlist() {
    try {
      const data = localStorage.getItem(WISHLIST_STORAGE_KEY);
      if (!data) return [];
      const parsed = JSON.parse(data);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.warn('BIBINONO: Could not parse wishlist from localStorage', e);
      return [];
    }
  }

  function setWishlist(items) {
    try {
      const cleanItems = Array.from(new Set(items));
      localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(cleanItems));
      syncWishlistBadges(cleanItems.length);
    } catch (e) {
      console.warn('BIBINONO: Could not save wishlist to localStorage', e);
    }
  }

  function syncWishlistBadges(count) {
    if (typeof count !== 'number') {
      count = getWishlist().length;
    }

    // Header count bubbles
    const headerCountEls = document.querySelectorAll('[data-wishlist-count]');
    headerCountEls.forEach((el) => {
      el.textContent = count;
    });

    const headerBubbleEls = document.querySelectorAll('[data-wishlist-count-bubble]');
    headerBubbleEls.forEach((bubble) => {
      bubble.style.display = count > 0 ? 'flex' : 'none';
    });

    // Mobile drawer count
    const drawerCountEls = document.querySelectorAll('[data-wishlist-drawer-count]');
    drawerCountEls.forEach((el) => {
      el.textContent = count;
      el.style.display = count > 0 ? 'inline-block' : 'none';
    });

    // Slide-out Favorites Drawer header badge
    const wishlistDrawerBadge = document.querySelector('[data-wishlist-drawer-badge]');
    if (wishlistDrawerBadge) {
      wishlistDrawerBadge.textContent = count;
      wishlistDrawerBadge.style.display = count > 0 ? 'inline-block' : 'none';
    }

    // Page count badges (if on /pages/wishlist)
    const pageTotalBadge = document.querySelector('[data-wishlist-total-badge]');
    const pageTotalNum = document.querySelector('[data-wishlist-total-num]');
    const pageActions = document.getElementById('bibinono-wishlist-actions');
    if (pageTotalNum) pageTotalNum.textContent = count;
    if (pageActions) pageActions.style.display = count > 0 ? 'flex' : 'none';
  }

  function syncWishlistButtons() {
    const wishlist = getWishlist();
    const buttons = document.querySelectorAll('[data-wishlist-btn]');
    const isArabic = document.documentElement.lang === 'ar' || document.documentElement.dir === 'rtl';

    buttons.forEach((btn) => {
      const handle = btn.getAttribute('data-product-handle');
      if (!handle) return;
      const isSaved = wishlist.includes(handle);
      btn.classList.toggle('is-active', isSaved);
      btn.setAttribute('aria-pressed', isSaved ? 'true' : 'false');

      const defaultAddText = isArabic ? 'أضف إلى المفضلة' : 'Save to Favorites';
      const defaultSavedText = isArabic ? 'في المفضلة' : 'Saved in Favorites';
      const textAdd = btn.getAttribute('data-text-add') || defaultAddText;
      const textSaved = btn.getAttribute('data-text-saved') || defaultSavedText;
      btn.setAttribute('aria-label', isSaved ? textSaved : textAdd);

      // Update text label if present (e.g. on PDP)
      const textEl = btn.querySelector('[data-wishlist-text]');
      if (textEl) {
        textEl.textContent = isSaved ? textSaved : textAdd;
      }
    });

    syncWishlistBadges(wishlist.length);
  }

  function isInWishlist(handle) {
    if (!handle) return false;
    return getWishlist().includes(handle);
  }

  function updateButtonStates(handle, isSaved) {
    const matchingButtons = document.querySelectorAll(`[data-wishlist-btn][data-product-handle="${handle}"]`);
    const isArabic = document.documentElement.lang === 'ar' || document.documentElement.dir === 'rtl';

    matchingButtons.forEach((b) => {
      b.classList.toggle('is-active', isSaved);
      b.setAttribute('aria-pressed', isSaved ? 'true' : 'false');

      const defaultAddText = isArabic ? 'أضف إلى المفضلة' : 'Save to Favorites';
      const defaultSavedText = isArabic ? 'في المفضلة' : 'Saved in Favorites';
      const textAdd = b.getAttribute('data-text-add') || defaultAddText;
      const textSaved = b.getAttribute('data-text-saved') || defaultSavedText;
      b.setAttribute('aria-label', isSaved ? textSaved : textAdd);

      const textEl = b.querySelector('[data-wishlist-text]');
      if (textEl) {
        textEl.textContent = isSaved ? textSaved : textAdd;
      }
    });
  }

  function notifyWishlistChange(handle, isSaved, btnEl) {
    const wishlist = getWishlist();
    const isArabic = document.documentElement.lang === 'ar' || document.documentElement.dir === 'rtl';

    let productTitle = '';
    if (btnEl) {
      productTitle = btnEl.getAttribute('data-product-title') || '';
    }
    if (!productTitle) {
      const matchingBtn = document.querySelector(`[data-wishlist-btn][data-product-handle="${handle}"][data-product-title]`);
      if (matchingBtn) productTitle = matchingBtn.getAttribute('data-product-title') || '';
    }

    if (isSaved) {
      const title = isArabic ? 'تمت الإضافة إلى المفضلة' : 'Saved to your favorites';
      showToast({
        title,
        message: productTitle || '',
        isRemoved: false,
      });
    } else {
      const title = isArabic ? 'تمت الإزالة من المفضلة' : 'Removed from your favorites';
      showToast({
        title,
        message: productTitle || '',
        linkUrl: false,
        isRemoved: true,
      });
    }

    document.dispatchEvent(
      new CustomEvent('bibinono:wishlist:updated', {
        detail: {
          handle,
          isSaved,
          count: wishlist.length,
          wishlist,
        },
      })
    );
  }

  function addToWishlist(handle, btnEl) {
    if (!handle) return false;
    let wishlist = getWishlist();
    if (!wishlist.includes(handle)) {
      wishlist.push(handle);
      setWishlist(wishlist);
      updateButtonStates(handle, true);
      notifyWishlistChange(handle, true, btnEl);
    }
    return true;
  }

  function removeFromWishlist(handle, btnEl) {
    if (!handle) return false;
    let wishlist = getWishlist();
    const index = wishlist.indexOf(handle);
    if (index !== -1) {
      wishlist.splice(index, 1);
      setWishlist(wishlist);
      updateButtonStates(handle, false);
      notifyWishlistChange(handle, false, btnEl);
    }
    return false;
  }

  function toggleWishlist(handle, btnEl) {
    if (!handle) return false;
    if (isInWishlist(handle)) {
      return removeFromWishlist(handle, btnEl);
    } else {
      return addToWishlist(handle, btnEl);
    }
  }

  let isWishlistBusy = false;
  let lastWishlistClick = 0;

  function handleWishlistClick(e) {
    const btn = e.target.closest('[data-wishlist-btn]');
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    const now = Date.now();
    if (isWishlistBusy || (now - lastWishlistClick < 350)) {
      return;
    }
    isWishlistBusy = true;
    lastWishlistClick = now;

    try {
      const handle = btn.getAttribute('data-product-handle');
      if (!handle) return;
      toggleWishlist(handle, btn);
    } finally {
      setTimeout(() => {
        isWishlistBusy = false;
      }, 350);
    }
  }

  // ── Gallery Dot Switcher ──
  function switchCardImage(cardMediaWrap, index) {
    if (!cardMediaWrap) return;

    const dots = cardMediaWrap.querySelectorAll('.bibinono-card__dot');
    const images = cardMediaWrap.querySelectorAll('.bibinono-card__img--gallery');
    const primaryImg = cardMediaWrap.querySelector('.bibinono-card__img--primary');

    dots.forEach((dot) => {
      const isCurrent = dot.getAttribute('data-media-index') === String(index);
      dot.classList.toggle('is-active', isCurrent);
      dot.setAttribute('aria-selected', isCurrent ? 'true' : 'false');
    });

    if (index === 0) {
      if (primaryImg) primaryImg.style.opacity = '1';
      images.forEach((img) => img.classList.remove('is-active'));
    } else {
      if (primaryImg) primaryImg.style.opacity = '0';
      images.forEach((img) => {
        const isCurrent = img.getAttribute('data-media-index') === String(index);
        img.classList.toggle('is-active', isCurrent);
      });
    }
  }

  function handleDotInteraction(e) {
    const dot = e.target.closest('.bibinono-card__dot');
    if (!dot) return;

    e.preventDefault();
    e.stopPropagation();

    const mediaWrap = dot.closest('.bibinono-card__media-wrap');
    const index = parseInt(dot.getAttribute('data-media-index') || '0', 10);
    switchCardImage(mediaWrap, index);
  }

  function setupMediaHoverReset() {
    document.addEventListener('mouseover', (e) => {
      const mediaWrap = e.target.closest('.bibinono-card__media-wrap');
      if (!mediaWrap || mediaWrap.dataset.hasResetListener) return;

      mediaWrap.dataset.hasResetListener = 'true';
      mediaWrap.addEventListener('mouseleave', () => {
        switchCardImage(mediaWrap, 0);
      });
    });
  }

  // ── Currency / Money Formatter ──
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const isArabic = document.documentElement.lang === 'ar' || document.documentElement.dir === 'rtl';
    const formatString = format || window.moneyFormat || (isArabic ? '{{amount}} £' : '£{{amount}}');

    function defaultOption(opt, def) {
      return typeof opt === 'undefined' ? def : opt;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');

      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const cents = parts[1] ? decimal + parts[1] : '';
      return dollars + cents;
    }

    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = formatString.match(placeholderRegex);
    if (!match) {
      const dollars = (cents / 100).toFixed(2);
      return isArabic ? `${dollars} £` : `£${dollars}`;
    }

    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  // ── Favorites Drawer Controller ──
  const cachedProducts = {};

  async function fetchProduct(handle) {
    if (cachedProducts[handle]) return cachedProducts[handle];
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return null;
      const data = await res.json();
      cachedProducts[handle] = data;
      return data;
    } catch (e) {
      console.warn(`BIBINONO: Could not fetch product ${handle}`, e);
      return null;
    }
  }

  function getWishlistDrawerEl() {
    return document.getElementById('BibinonoWishlistDrawer');
  }

  function openWishlistDrawer() {
    const drawer = getWishlistDrawerEl();
    if (!drawer) return;

    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('bibinono-drawer-open');
    document.body.style.overflow = 'hidden';

    // If Dawn mobile menu drawer is open, close it
    const menuDrawer = document.querySelector('header-drawer details[open]');
    if (menuDrawer) {
      menuDrawer.removeAttribute('open');
    }

    renderWishlistDrawer();
  }

  function closeWishlistDrawer() {
    const drawer = getWishlistDrawerEl();
    if (!drawer) return;

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('bibinono-drawer-open');
    document.body.style.overflow = '';
  }

  async function renderWishlistDrawer() {
    const drawer = getWishlistDrawerEl();
    if (!drawer) return;

    const listEl = drawer.querySelector('[data-wishlist-drawer-list]');
    const skeletonEl = drawer.querySelector('[data-wishlist-drawer-skeleton]');
    const emptyEl = drawer.querySelector('[data-wishlist-drawer-empty]');
    const footerEl = drawer.querySelector('[data-wishlist-drawer-footer]');
    const badgeEl = drawer.querySelector('[data-wishlist-drawer-badge]');

    const handles = getWishlist();
    const count = handles.length;

    if (badgeEl) {
      badgeEl.textContent = count;
      badgeEl.style.display = count > 0 ? 'inline-block' : 'none';
    }

    if (count === 0) {
      if (skeletonEl) skeletonEl.style.display = 'none';
      if (listEl) {
        listEl.innerHTML = '';
        listEl.style.display = 'none';
      }
      if (footerEl) footerEl.style.display = 'none';
      if (emptyEl) emptyEl.style.display = 'flex';
      return;
    }

    if (emptyEl) emptyEl.style.display = 'none';
    if (skeletonEl) skeletonEl.style.display = 'flex';
    if (listEl) listEl.style.display = 'none';
    if (footerEl) footerEl.style.display = 'none';

    const productPromises = handles.map((h) => fetchProduct(h));
    const results = await Promise.allSettled(productPromises);
    const validProducts = results
      .filter((r) => r.status === 'fulfilled' && r.value !== null)
      .map((r) => r.value);

    if (skeletonEl) skeletonEl.style.display = 'none';

    if (validProducts.length === 0) {
      if (listEl) {
        listEl.innerHTML = '';
        listEl.style.display = 'none';
      }
      if (footerEl) footerEl.style.display = 'none';
      if (emptyEl) emptyEl.style.display = 'flex';
      return;
    }

    const isArabic = document.documentElement.lang === 'ar' || document.documentElement.dir === 'rtl';
    const addToBagText = isArabic ? 'أضف للسلة' : 'Add to Bag';
    const soldOutText = isArabic ? 'نفد المخزون' : 'Sold Out';
    const chooseOptionsText = isArabic ? 'اختر الخيارات' : 'Choose Options';
    const removeLabel = isArabic ? 'إزالة' : 'Remove';

    const html = validProducts
      .map((product) => {
        const hasMultipleVariants = product.variants.length > 1;
        const firstVariant = product.variants[0];
        const isAvailable = product.available;
        const onSale = product.compare_at_price > product.price;
        const featuredImage = product.featured_image || (product.images && product.images[0]) || '';
        const priceFormatted = formatMoney(product.price);
        const comparePriceFormatted = onSale ? formatMoney(product.compare_at_price) : '';

        let ctaHtml = '';
        if (!isAvailable) {
          ctaHtml = `<button type="button" class="bibinono-wishlist-drawer__add-btn" disabled style="opacity: 0.5;">${soldOutText}</button>`;
        } else if (hasMultipleVariants) {
          ctaHtml = `<a href="${product.url}" class="bibinono-wishlist-drawer__add-btn" style="text-decoration: none;">${chooseOptionsText}</a>`;
        } else {
          ctaHtml = `
            <button
              type="button"
              class="bibinono-wishlist-drawer__add-btn"
              data-wishlist-drawer-add-to-cart
              data-variant-id="${firstVariant.id}"
            >
              <span>${addToBagText}</span>
              <span class="bibinono-wishlist-drawer__spinner" style="display: none;" aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="animate-spin"><path d="M21 12a9 9 0 1 1-6.219-8.56"></path></svg>
              </span>
            </button>
          `;
        }

        return `
          <div class="bibinono-wishlist-drawer__item" data-drawer-product-handle="${product.handle}">
            <a href="${product.url}" class="bibinono-wishlist-drawer__thumb-link" tabindex="-1">
              ${
                featuredImage
                  ? `<img src="${featuredImage}" alt="${product.title.replace(/"/g, '&quot;')}" class="bibinono-wishlist-drawer__thumb-img" loading="lazy" width="80" height="80">`
                  : `<div style="width:100%;height:100%;background:#F0ECE6;"></div>`
              }
            </a>
            <div class="bibinono-wishlist-drawer__item-info">
              ${product.vendor ? `<span class="bibinono-wishlist-drawer__item-vendor">${product.vendor}</span>` : ''}
              <a href="${product.url}" class="bibinono-wishlist-drawer__item-title">${product.title}</a>
              <div class="bibinono-wishlist-drawer__item-price">
                <span>${priceFormatted}</span>
                ${onSale ? `<s>${comparePriceFormatted}</s>` : ''}
              </div>
              <div class="bibinono-wishlist-drawer__item-actions">
                ${ctaHtml}
              </div>
            </div>
            <button
              type="button"
              class="bibinono-wishlist-drawer__remove-btn"
              data-wishlist-drawer-remove
              data-product-handle="${product.handle}"
              aria-label="${removeLabel}"
              title="${removeLabel}"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#E54848" stroke="#E54848" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
              </svg>
            </button>
          </div>
        `;
      })
      .join('');

    if (listEl) {
      listEl.innerHTML = html;
      listEl.style.display = 'flex';
    }
    if (footerEl) {
      footerEl.style.display = 'flex';
    }
  }

  async function handleDrawerAddToCart(button) {
    const variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;

    const spanText = button.querySelector('span:first-child');
    const spinner = button.querySelector('.bibinono-wishlist-drawer__spinner');
    if (spanText) spanText.style.display = 'none';
    if (spinner) spinner.style.display = 'inline-block';
    button.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: 'cart-icon-bubble',
        }),
      });

      const data = await response.json();
      if (data.status) {
        throw new Error(data.description || 'Could not add to cart');
      }

      // Update cart bubble
      if (data.sections && data.sections['cart-icon-bubble']) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(data.sections['cart-icon-bubble'], 'text/html');
        const newBubble = doc.querySelector('#cart-icon-bubble') || doc.body.firstElementChild;
        const currentBubble = document.getElementById('cart-icon-bubble');
        if (newBubble && currentBubble) {
          currentBubble.innerHTML = newBubble.innerHTML;
        }
      } else {
        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        const countEls = document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]');
        countEls.forEach((el) => {
          el.textContent = cart.item_count;
        });
      }

      // Close wishlist drawer and open cart drawer
      closeWishlistDrawer();
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }

      const isArabic = document.documentElement.lang === 'ar' || document.documentElement.dir === 'rtl';
      showToast({
        title: isArabic ? 'تمت إضافة المنتج إلى السلة' : 'Added to your nursery cart',
        linkText: isArabic ? 'عرض السلة' : 'View Cart',
        linkUrl: '/cart',
      });
    } catch (err) {
      console.error('BIBINONO: Drawer add to cart failed', err);
      const isArabic = document.documentElement.lang === 'ar' || document.documentElement.dir === 'rtl';
      showToast({
        title: isArabic ? 'تعذر إضافة المنتج' : 'Could not add to cart',
        message: err.message,
        isError: true,
        linkUrl: false,
      });
    } finally {
      if (spanText) spanText.style.display = 'inline-block';
      if (spinner) spinner.style.display = 'none';
      button.disabled = false;
    }
  }

  function handleDrawerRemove(button) {
    const handle = button.getAttribute('data-product-handle');
    if (!handle) return;

    const row = button.closest('.bibinono-wishlist-drawer__item');
    if (row) {
      row.style.transition = 'transform 200ms ease, opacity 200ms ease, height 200ms ease';
      row.style.transform = 'translateX(20px)';
      row.style.opacity = '0';
    }

    setTimeout(() => {
      if (row) row.remove();
      removeFromWishlist(handle);

      const drawer = getWishlistDrawerEl();
      if (!drawer) return;
      const remaining = drawer.querySelectorAll('.bibinono-wishlist-drawer__item');
      if (remaining.length === 0) {
        const listEl = drawer.querySelector('[data-wishlist-drawer-list]');
        const emptyEl = drawer.querySelector('[data-wishlist-drawer-empty]');
        const footerEl = drawer.querySelector('[data-wishlist-drawer-footer]');
        if (listEl) listEl.style.display = 'none';
        if (footerEl) footerEl.style.display = 'none';
        if (emptyEl) emptyEl.style.display = 'flex';
      }
    }, 200);
  }

  function handleDrawerClearAll() {
    const isArabic = document.documentElement.lang === 'ar' || document.documentElement.dir === 'rtl';
    const confirmMsg = isArabic
      ? 'هل أنت متأكد من مسح جميع المنتجات من قائمتك المفضلة؟'
      : 'Are you sure you want to clear all items from your favorites?';

    if (!window.confirm(confirmMsg)) return;

    setWishlist([]);
    syncWishlistButtons();

    const drawer = getWishlistDrawerEl();
    if (drawer) {
      const listEl = drawer.querySelector('[data-wishlist-drawer-list]');
      const emptyEl = drawer.querySelector('[data-wishlist-drawer-empty]');
      const footerEl = drawer.querySelector('[data-wishlist-drawer-footer]');
      if (listEl) {
        listEl.innerHTML = '';
        listEl.style.display = 'none';
      }
      if (footerEl) footerEl.style.display = 'none';
      if (emptyEl) emptyEl.style.display = 'flex';
    }

    showToast({
      title: isArabic ? 'تم مسح المفضلة' : 'Favorites cleared',
      linkUrl: false,
    });
  }

  // ── Public API on window ──
  window.BibinonoWishlist = {
    getWishlist,
    setWishlist,
    isInWishlist,
    addToWishlist,
    removeFromWishlist,
    toggleWishlist,
    syncWishlistButtons,
    syncWishlistBadges,
    showToast,
    openDrawer: openWishlistDrawer,
    closeDrawer: closeWishlistDrawer,
    renderDrawer: renderWishlistDrawer,
  };

  // ── Profile Switcher Manager ──
  const PROFILE_STORAGE_KEY = 'bibinono_active_profile';

  function initProfileSwitcher() {
    const switcher = document.querySelector('[data-bibinono-profile-switcher]');
    if (!switcher) return;

    const btn = switcher.querySelector('#bibinonoProfileBtn');
    const dropdown = switcher.querySelector('#bibinonoProfileDropdown');
    const nameEl = switcher.querySelector('[data-active-name]');
    const avatarEl = switcher.querySelector('[data-active-avatar]');
    const items = switcher.querySelectorAll('.bibinono-profile-dropdown__item');

    if (!btn || !dropdown) return;

    // Restore saved profile
    try {
      const saved = localStorage.getItem(PROFILE_STORAGE_KEY);
      if (saved) {
        const data = JSON.parse(saved);
        if (data.name && nameEl) nameEl.textContent = data.name;
        if (data.avatar && avatarEl) avatarEl.src = data.avatar;
        items.forEach((it) => {
          const isCurrent = it.getAttribute('data-profile-id') === data.id;
          it.classList.toggle('is-selected', isCurrent);
          it.setAttribute('aria-checked', isCurrent ? 'true' : 'false');
        });
      }
    } catch (e) {}

    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = dropdown.style.display === 'block';
      dropdown.style.display = isOpen ? 'none' : 'block';
      btn.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    });

    items.forEach((item) => {
      item.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = item.getAttribute('data-profile-id');
        const name = item.getAttribute('data-name');
        const avatar = item.getAttribute('data-avatar');

        items.forEach((it) => {
          it.classList.remove('is-selected');
          it.setAttribute('aria-checked', 'false');
        });
        item.classList.add('is-selected');
        item.setAttribute('aria-checked', 'true');

        if (name && nameEl) nameEl.textContent = name;
        if (avatar && avatarEl) avatarEl.src = avatar;

        dropdown.style.display = 'none';
        btn.setAttribute('aria-expanded', 'false');

        try {
          localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify({ id, name, avatar }));
        } catch (e) {}

        document.dispatchEvent(new CustomEvent('bibinono:profile:changed', { detail: { id, name, avatar } }));
      });
    });

    document.addEventListener('click', (e) => {
      if (!switcher.contains(e.target)) {
        dropdown.style.display = 'none';
        btn.setAttribute('aria-expanded', 'false');
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        dropdown.style.display = 'none';
        btn.setAttribute('aria-expanded', 'false');
      }
    });
  }

  // ── Tooltips Tap & Keyboard Handler ──
  function initTooltips() {
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('.bibinono-tooltip-trigger');
      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        const popover = trigger.nextElementSibling;
        if (popover && popover.classList.contains('bibinono-tooltip-popover')) {
          const isOpen = popover.classList.contains('is-open');
          document.querySelectorAll('.bibinono-tooltip-popover.is-open').forEach((p) => p.classList.remove('is-open'));
          if (!isOpen) popover.classList.add('is-open');
        }
        return;
      }

      // Close all tooltips on outside click
      if (!e.target.closest('.bibinono-tooltip-popover')) {
        document.querySelectorAll('.bibinono-tooltip-popover.is-open').forEach((p) => p.classList.remove('is-open'));
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        document.querySelectorAll('.bibinono-tooltip-popover.is-open').forEach((p) => p.classList.remove('is-open'));
      }
    });
  }

  // ── Initialisation & Dynamic Observer ──
  let isInitialized = false;
  function init() {
    if (isInitialized) return;
    isInitialized = true;

    syncWishlistButtons();
    setupMediaHoverReset();
    initProfileSwitcher();
    initTooltips();

    // Event delegation for clicks (wishlist, dots, drawer triggers & actions)
    document.addEventListener('click', (e) => {
      // Open Wishlist Drawer
      const openTrigger = e.target.closest('#wishlist-icon-bubble, [data-bibinono-wishlist-drawer-open]');
      if (openTrigger) {
        e.preventDefault();
        openWishlistDrawer();
        return;
      }

      // Close Wishlist Drawer
      const closeTrigger = e.target.closest('[data-wishlist-drawer-close]');
      if (closeTrigger) {
        e.preventDefault();
        closeWishlistDrawer();
        return;
      }

      // Drawer: Add to Cart
      const drawerAddBtn = e.target.closest('[data-wishlist-drawer-add-to-cart]');
      if (drawerAddBtn) {
        e.preventDefault();
        handleDrawerAddToCart(drawerAddBtn);
        return;
      }

      // Drawer: Remove Item
      const drawerRemoveBtn = e.target.closest('[data-wishlist-drawer-remove]');
      if (drawerRemoveBtn) {
        e.preventDefault();
        handleDrawerRemove(drawerRemoveBtn);
        return;
      }

      // Drawer: Clear All
      const drawerClearBtn = e.target.closest('[data-wishlist-drawer-clear]');
      if (drawerClearBtn) {
        e.preventDefault();
        handleDrawerClearAll();
        return;
      }

      if (e.target.closest('[data-wishlist-btn]')) {
        handleWishlistClick(e);
      } else if (e.target.closest('.bibinono-card__dot')) {
        handleDotInteraction(e);
      }
    });

    // Close drawer on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeWishlistDrawer();
      }
    });

    // Hover on dots switches image instantly on pointer devices
    document.addEventListener('mouseover', (e) => {
      if (e.target.closest('.bibinono-card__dot')) {
        handleDotInteraction(e);
      }
    });

    // Cross-tab synchronization
    window.addEventListener('storage', (e) => {
      if (e.key === WISHLIST_STORAGE_KEY) {
        syncWishlistButtons();
      }
    });

    // Re-sync on Shopify theme section loads / AJAX collection filters
    document.addEventListener('shopify:section:load', () => {
      syncWishlistButtons();
      initProfileSwitcher();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
